import { ConsentFormDefinition } from '../types';

export const hipaaPrivacyNotice: ConsentFormDefinition = {
  formKey: 'hipaa-privacy-notice',
  title: 'HIPAA Privacy Notice & Consent',
  pdfTitle: 'Notice of Privacy Practices & Consent to Use Health Information',
  intro:
    'This notice explains how {providerName} uses and protects your health information, and what rights you have as a patient.',
  sections: [
    {
      id: 'patient',
      navLabel: 'Patient Info',
      heading: 'Your Information',
      blocks: [
        {
          type: 'field-group',
          layout: '2-1',
          fields: [
            {
              type: 'text',
              id: 'firstName',
              label: 'First Name',
              placeholder: 'First name',
              required: true,
            },
            {
              type: 'text',
              id: 'middleInitial',
              label: 'Middle Initial (optional)',
              placeholder: 'M.I.',
            },
            {
              type: 'text',
              id: 'lastName',
              label: 'Last Name',
              placeholder: 'Last name',
              required: true,
            },
            {
              type: 'date',
              id: 'dob',
              label: 'Date of Birth',
              required: true,
            },
          ],
        },
      ],
    },
    {
      id: 'what-is-phi',
      navLabel: 'What Is PHI?',
      heading: 'What Is Your Health Information?',
      blocks: [
        {
          type: 'paragraph',
          text: 'Protected Health Information (PHI) is any information about you created or received in connection with your care. This includes:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Your name, date of birth, and contact information',
            'Your diagnosis and treatment plan',
            'Therapy notes and counseling session records',
            'Medication records and prescriptions',
            'Insurance information and billing records',
            'Medical test results and evaluations',
          ],
        },
        {
          type: 'paragraph',
          text: 'We keep this information confidential and protected under federal law (HIPAA) and state privacy laws.',
        },
      ],
    },
    {
      id: 'how-we-use',
      navLabel: 'How We Use Your Info',
      heading: 'How We Use Your Health Information',
      blocks: [
        {
          type: 'paragraph',
          text: 'We use your health information for three main purposes, without needing your permission each time:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Treatment: To provide you with therapy, counseling, psychiatric care, or medication management',
            'Payment: To bill your insurance, collect copays, or process payment for services',
            'Operations: To improve quality, train staff, manage our practice, and comply with laws',
          ],
        },
        {
          type: 'paragraph',
          text: 'We will not sell your information, use it for marketing without your permission, or share it with your employer or family members without your written consent.',
        },
      ],
    },
    {
      id: 'when-we-share',
      navLabel: 'When We Share Info',
      heading: 'When We Must or May Share Your Information',
      blocks: [
        {
          type: 'paragraph',
          text: 'By law, we must share your information in these situations:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You tell us you plan to hurt yourself or someone else — we may contact emergency services or notify the person at risk',
            'We believe a child, elder, or dependent adult is being abused or neglected — we must report to authorities',
            'A court orders us to share information through a legal subpoena or warrant',
            'Government agencies need information for licensing, audits, or investigations',
          ],
        },
        {
          type: 'paragraph',
          text: 'We may also share your information with:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Other healthcare providers involved in your care (with your permission)',
            'Your health insurance company to process claims',
            'Your supervisor or emergency contact only with your written permission',
            'Our business partners who help us (under strict confidentiality agreements)',
          ],
        },
      ],
    },
    {
      id: 'special-protections',
      navLabel: 'Special Protections',
      heading: 'Special Protections for Sensitive Information',
      blocks: [
        {
          type: 'paragraph',
          text: 'Some information has extra protection:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Substance Use Disorder Treatment: If you receive treatment for substance use, additional federal law (42 CFR Part 2) protects your privacy. We cannot share this information without your written permission, even with your other doctors.',
            'Psychotherapy Notes: Session notes that are kept separate from your main health record have extra protection under HIPAA.',
            'Minors: Depending on your state, some health information about teenagers may be kept confidential from parents or guardians.',
          ],
        },
      ],
    },
    {
      id: 'security',
      navLabel: 'How We Protect You',
      heading: 'How We Protect Your Information',
      blocks: [
        {
          type: 'paragraph',
          text: 'We use several methods to keep your health information secure:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Encryption: All information sent through our telehealth platform is encrypted, so no one can intercept it',
            'Secure Storage: Your records are stored on secure, HIPAA-compliant servers with automatic backups',
            'Access Controls: Only authorized staff can access your information, and all access is logged',
            'Staff Training: Our team is trained on confidentiality and privacy requirements',
            'Business Associate Agreements: Any company we work with signs a legal agreement to protect your privacy',
          ],
        },
        {
          type: 'paragraph',
          text: 'We do not record your sessions without your written permission. If we provide supervision or consultation with other therapists, we will de-identify your information when possible.',
        },
      ],
    },
    {
      id: 'your-rights',
      navLabel: 'Your Rights',
      heading: 'Your Privacy Rights',
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the following rights regarding your health information:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Right to Access: Request a copy of your health records in paper or electronic format within 30 days',
            'Right to Amend: Ask us to correct or update inaccurate information in your record',
            'Right to an Accounting: Ask for a list of who has received your information in the past 6 years',
            'Right to Request Restrictions: Ask us to limit how we use or share your information (though we may not always agree)',
            'Right to Confidential Communications: Request that we contact you only at a specific phone number, address, or email',
            'Right to a Copy of This Notice: Ask for a printed copy of this privacy notice at any time',
            'Right to File a Complaint: Contact your state licensing board or the U.S. Department of Health and Human Services if you believe your rights have been violated',
          ],
        },
      ],
    },
    {
      id: 'changes',
      navLabel: 'Changes to This Notice',
      heading: 'Changes to This Privacy Notice',
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this notice at any time. Changes apply to all your health information, whether we created it before or after the change. You can always request the current version from our office or the HIPAA LINK platform.',
        },
      ],
    },
    {
      id: 'breach',
      navLabel: 'Data Breach Notification',
      heading: 'What Happens If Your Information Is Breached',
      blocks: [
        {
          type: 'paragraph',
          text: 'If we discover that unsecured health information about you has been accessed, lost, or stolen, we will notify you within 60 days. The notification will include:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'A description of what happened',
            'What types of information were involved',
            'Steps you can take to protect yourself',
            'What we are doing to investigate and prevent future breaches',
            'Contact information for questions',
          ],
        },
      ],
    },
    {
      id: 'understand',
      navLabel: 'Before You Sign',
      heading: 'Before You Sign',
      blocks: [
        {
          type: 'paragraph',
          text: 'By signing this form, you are saying:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You have read and understand this Notice of Privacy Practices',
            'You understand how your health information will be used and who it may be shared with',
            'You understand your privacy rights and how to exercise them',
            'You understand the limits of confidentiality (duty to warn, abuse reporting, court orders)',
            'You authorize us to use your health information for treatment, payment, and operations',
            'You understand you can revoke this consent in writing at any time, except for actions already taken',
          ],
        },
        {
          type: 'paragraph',
          text: 'If you do not agree, please discuss your concerns with your provider before signing.',
        },
      ],
    },
    {
      id: 'sign',
      navLabel: 'Signature',
      heading: 'Your Signature',
      blocks: [
        {
          type: 'field-group',
          fields: [
            {
              type: 'text',
              id: 'printedName',
              label: 'Your Name (print)',
              placeholder: 'Print your name',
              required: true,
              fullWidth: true,
            },
            {
              type: 'select',
              id: 'relationship',
              label: 'Signing as:',
              options: [
                { value: 'self', label: 'Myself (the patient)' },
                { value: 'parent', label: 'Parent of patient' },
                { value: 'guardian', label: 'Legal Guardian' },
                { value: 'poa', label: 'Power of Attorney' },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'By signing below, I confirm that I have read and understand this Notice of Privacy Practices, and I authorize this practice to use and disclose my health information as described above.',
        },
        { type: 'signature' },
      ],
    },
  ],
  requiredFields: ['firstName', 'lastName', 'dob', 'signature', 'printedName'],
};
